import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { 
  Plus, Trash, PencilSimple, ArrowLeft, FloppyDisk, Eye,
  FileText, CircleNotch, Globe, Code, ArrowsOutSimple
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import RichTextEditor from '../../components/RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ======== HTML EDITOR with Visual/Code toggle ========
const HtmlEditor = ({ value, onChange }) => {
  const [viewMode, setViewMode] = useState('visual'); // visual, code

  return (
    <div data-testid="html-editor">
      <div className="flex items-center gap-1 p-2 bg-slate-50 border border-b-0 border-slate-200 rounded-t-lg">
        <button
          onClick={() => setViewMode('visual')}
          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${viewMode === 'visual' ? 'bg-white border shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          <Eye size={12} className="inline mr-1" />Visuel
        </button>
        <button
          onClick={() => setViewMode('code')}
          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${viewMode === 'code' ? 'bg-white border shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          <Code size={12} className="inline mr-1" />HTML
        </button>
      </div>
      {viewMode === 'code' ? (
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full min-h-[400px] p-4 font-mono text-sm focus:outline-none resize-y border border-slate-200 rounded-b-lg"
          placeholder="<div>Contenu HTML...</div>"
          data-testid="html-code-editor"
        />
      ) : (
        <div className="[&>div]:rounded-t-none [&>div]:border-t-0">
          <RichTextEditor content={value || ''} onChange={onChange} placeholder="Commencez à écrire le contenu..." minHeight={400} />
        </div>
      )}
    </div>
  );
};

// ======== PAGE DATA EDITOR (Hero images + Timeline for "le-magasin") ========
const MAGASIN_DEFAULTS = {
  hero_subtitle: "Une passion familiale pour la viande d'exception depuis 1985",
  story_title: 'Notre histoire',
  story_text: "Depuis plus de 30 ans, la famille Magere perpétue un savoir-faire artisanal d'exception au cœur du Vaucluse. Fondée par la passion de la viande et le respect du produit, notre boucherie est devenue une référence incontournable pour les amateurs de viandes de qualité supérieure.",
  quality_kicker: 'Notre savoir-faire',
  quality_title: 'Engagement qualité',
  quality_text1: "Chez Magere Viandes, chaque pièce est sélectionnée avec la plus grande exigence. Nous travaillons en direct avec des éleveurs locaux et des producteurs français reconnus.",
  quality_text2: "Notre bœuf est maturé sur os pendant 21 à 45 jours dans nos caves de maturation, pour développer des saveurs incomparables.",
};
const MAGASIN_DEFAULT_STATS = [
  { value: '30+', label: "ans d'expérience" },
  { value: '45j', label: 'de maturation max' },
  { value: '35', label: 'communes livrées' },
  { value: '100€', label: 'livraison offerte dès' },
];

const PageDataEditor = ({ pageData, onChange }) => {
  const pd = pageData || { hero_images: [], timeline: [], values: [], hours: [] };
  const v = (field) => (pd[field] !== undefined && pd[field] !== '' ? pd[field] : (MAGASIN_DEFAULTS[field] || ''));
  const stats = (pd.stats && pd.stats.length ? pd.stats : MAGASIN_DEFAULT_STATS);
  const updStat = (i, field, value) => {
    const next = stats.map((s, x) => (x === i ? { ...s, [field]: value } : s));
    onChange({ ...pd, stats: next });
  };

  const updateField = (section, index, field, value) => {
    const updated = { ...pd };
    updated[section] = [...(updated[section] || [])];
    updated[section][index] = { ...updated[section][index], [field]: value };
    onChange(updated);
  };

  const addItem = (section, template) => {
    const updated = { ...pd, [section]: [...(pd[section] || []), template] };
    onChange(updated);
  };

  const removeItem = (section, index) => {
    const updated = { ...pd, [section]: (pd[section] || []).filter((_, i) => i !== index) };
    onChange(updated);
  };

  return (
    <div className="space-y-6" data-testid="page-data-editor">
      {/* Textes de la page */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Textes de la page</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Ces textes sont affichés sur la page du site. Modifiez-les puis Enregistrez.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Sous-titre du haut de page</Label>
            <Input value={v('hero_subtitle')} onChange={e => onChange({ ...pd, hero_subtitle: e.target.value })} className="text-sm" data-testid="magasin-hero-subtitle" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Titre « Notre histoire »</Label>
              <Input value={v('story_title')} onChange={e => onChange({ ...pd, story_title: e.target.value })} className="text-sm" data-testid="magasin-story-title" />
            </div>
            <div>
              <Label className="text-xs">Titre « Engagement qualité »</Label>
              <Input value={v('quality_title')} onChange={e => onChange({ ...pd, quality_title: e.target.value })} className="text-sm" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Texte « Notre histoire »</Label>
            <textarea value={v('story_text')} onChange={e => onChange({ ...pd, story_text: e.target.value })}
              className="w-full text-sm p-2 border rounded-lg min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="magasin-story-text" />
          </div>
          <div>
            <Label className="text-xs">Petit titre au-dessus d'« Engagement qualité »</Label>
            <Input value={v('quality_kicker')} onChange={e => onChange({ ...pd, quality_kicker: e.target.value })} className="text-sm" />
          </div>
          <div>
            <Label className="text-xs">Texte qualité — paragraphe 1</Label>
            <textarea value={v('quality_text1')} onChange={e => onChange({ ...pd, quality_text1: e.target.value })}
              className="w-full text-sm p-2 border rounded-lg min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="magasin-quality-text1" />
          </div>
          <div>
            <Label className="text-xs">Texte qualité — paragraphe 2</Label>
            <textarea value={v('quality_text2')} onChange={e => onChange({ ...pd, quality_text2: e.target.value })}
              className="w-full text-sm p-2 border rounded-lg min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <Label className="text-xs">Chiffres clés (4 encadrés)</Label>
            <div className="space-y-2 mt-1">
              {stats.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={s.value} onChange={e => updStat(i, 'value', e.target.value)} placeholder="30+" className="text-sm w-24" data-testid={`magasin-stat-value-${i}`} />
                  <Input value={s.label} onChange={e => updStat(i, 'label', e.target.value)} placeholder="ans d'expérience" className="text-sm flex-1" />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* YouTube Video */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Vidéo de présentation (YouTube)</CardTitle>
        </CardHeader>
        <CardContent>
          <Input value={pd.youtube_video_id || ''} onChange={e => onChange({ ...pd, youtube_video_id: e.target.value })} placeholder="ID de la vidéo YouTube (ex: dQw4w9WgXcQ)" className="text-sm" data-testid="youtube-video-input" />
          <p className="text-xs text-slate-400 mt-1.5">Collez l'ID de la vidéo YouTube (la partie après v= dans l'URL)</p>
        </CardContent>
      </Card>

      {/* Hero Images */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Carrousel Hero</CardTitle>
            <Button size="sm" variant="outline" onClick={() => addItem('hero_images', { url: '', alt: '', caption: '' })}>
              <Plus size={14} className="mr-1" /> Image
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {(pd.hero_images || []).map((img, i) => (
            <div key={i} className="flex gap-2 items-start p-3 bg-slate-50 rounded-lg" data-testid={`hero-image-${i}`}>
              {img.url && <img src={img.url} alt="" className="w-16 h-12 object-cover rounded flex-shrink-0" />}
              <div className="flex-1 space-y-1.5">
                <Input value={img.url} onChange={e => updateField('hero_images', i, 'url', e.target.value)} placeholder="URL de l'image" className="text-xs h-8" />
                <div className="grid grid-cols-2 gap-1.5">
                  <Input value={img.alt || ''} onChange={e => updateField('hero_images', i, 'alt', e.target.value)} placeholder="Alt text" className="text-xs h-8" />
                  <Input value={img.caption || ''} onChange={e => updateField('hero_images', i, 'caption', e.target.value)} placeholder="Légende" className="text-xs h-8" />
                </div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => removeItem('hero_images', i)} className="flex-shrink-0 h-8 w-8">
                <Trash size={14} className="text-red-500" />
              </Button>
            </div>
          ))}
          {(pd.hero_images || []).length === 0 && <p className="text-xs text-slate-400 text-center py-2">Aucune image hero</p>}
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Timeline historique</CardTitle>
            <Button size="sm" variant="outline" onClick={() => addItem('timeline', { year: '', title: '', description: '', image_url: '' })}>
              <Plus size={14} className="mr-1" /> Événement
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {(pd.timeline || []).map((entry, i) => (
            <div key={i} className="p-3 bg-slate-50 rounded-lg space-y-1.5" data-testid={`timeline-entry-${i}`}>
              <div className="flex gap-2">
                <Input value={entry.year} onChange={e => updateField('timeline', i, 'year', e.target.value)} placeholder="Année" className="text-xs h-8 w-20" />
                <Input value={entry.title} onChange={e => updateField('timeline', i, 'title', e.target.value)} placeholder="Titre" className="text-xs h-8 flex-1" />
                <Button size="icon" variant="ghost" onClick={() => removeItem('timeline', i)} className="h-8 w-8 flex-shrink-0">
                  <Trash size={14} className="text-red-500" />
                </Button>
              </div>
              <textarea value={entry.description} onChange={e => updateField('timeline', i, 'description', e.target.value)} placeholder="Description..." className="w-full text-xs p-2 border rounded min-h-[50px] focus:outline-none focus:ring-1 focus:ring-blue-500" />
              <Input value={entry.image_url || ''} onChange={e => updateField('timeline', i, 'image_url', e.target.value)} placeholder="URL de la photo" className="text-xs h-8" />
            </div>
          ))}
          {(pd.timeline || []).length === 0 && <p className="text-xs text-slate-400 text-center py-2">Aucun événement</p>}
        </CardContent>
      </Card>

      {/* Values */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Valeurs</CardTitle>
            <Button size="sm" variant="outline" onClick={() => addItem('values', { icon: 'leaf', title: '', description: '' })}>
              <Plus size={14} className="mr-1" /> Valeur
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {(pd.values || []).map((v, i) => (
            <div key={i} className="flex gap-2 items-start p-3 bg-slate-50 rounded-lg" data-testid={`value-${i}`}>
              <select value={v.icon} onChange={e => updateField('values', i, 'icon', e.target.value)} className="text-xs h-8 border rounded px-1.5">
                <option value="leaf">Feuille</option>
                <option value="heart">Cœur</option>
                <option value="knife">Couteau</option>
                <option value="users">Équipe</option>
              </select>
              <div className="flex-1 space-y-1.5">
                <Input value={v.title} onChange={e => updateField('values', i, 'title', e.target.value)} placeholder="Titre" className="text-xs h-8" />
                <Input value={v.description} onChange={e => updateField('values', i, 'description', e.target.value)} placeholder="Description" className="text-xs h-8" />
              </div>
              <Button size="icon" variant="ghost" onClick={() => removeItem('values', i)} className="h-8 w-8 flex-shrink-0">
                <Trash size={14} className="text-red-500" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Hours */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Horaires</CardTitle>
            <Button size="sm" variant="outline" onClick={() => addItem('hours', { day: '', hours: '' })}>
              <Plus size={14} className="mr-1" /> Créneau
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {(pd.hours || []).map((h, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`hour-${i}`}>
              <Input value={h.day} onChange={e => updateField('hours', i, 'day', e.target.value)} placeholder="Jour(s)" className="text-xs h-8 w-40" />
              <Input value={h.hours} onChange={e => updateField('hours', i, 'hours', e.target.value)} placeholder="Horaires" className="text-xs h-8 flex-1" />
              <Button size="icon" variant="ghost" onClick={() => removeItem('hours', i)} className="h-8 w-8 flex-shrink-0">
                <Trash size={14} className="text-red-500" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// ======== CONTACT DATA EDITOR (textes + horaires page "contact") ========
const CONTACT_DEFAULTS = {
  intro_text: "Une question, une commande spéciale, un devis professionnel ? Notre équipe est à votre écoute.",
  phone_note: 'Du mardi au samedi',
  email_note: 'Réponse sous 24h',
  parking_title: 'Grand parking gratuit',
  parking_text: 'Accès facile et gratuit',
  parking_note: 'Plus de 50 places disponibles',
  access_title: "Accès depuis l'autoroute",
  access_text: 'Sortie autoroute A7, direction centre-ville. Suivre la signalétique "MAGERE Viandes".',
  parking_detail: 'Grand parking gratuit de plus de 50 places juste devant le magasin. Accès facile même en véhicule utilitaire pour les professionnels.',
  delivery_title: 'Livraison à domicile',
  delivery_footnote: "La liste des communes desservies s'affiche automatiquement au moment de la commande selon votre code postal.",
  pro_title: 'Vous êtes un professionnel ?',
  pro_text: "Restaurateurs, traiteurs, collectivités : bénéficiez de nos tarifs professionnels, de la livraison prioritaire et d'un interlocuteur dédié pour vos commandes en gros et demi-gros.",
};

const ContactDataEditor = ({ pageData, onChange }) => {
  const pd = pageData || {};
  const set = (field, value) => onChange({ ...pd, [field]: value });
  // Affiche la valeur réellement visible sur le site (défaut si non personnalisée)
  const v = (field) => (pd[field] !== undefined && pd[field] !== '' ? pd[field] : (CONTACT_DEFAULTS[field] || ''));

  return (
    <div className="space-y-6" data-testid="contact-data-editor">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Texte de présentation</CardTitle></CardHeader>
        <CardContent>
          <textarea value={v('intro_text')} onChange={e => set('intro_text', e.target.value)}
            placeholder="Une question, une commande spéciale, un devis professionnel ? Notre équipe est à votre écoute."
            className="w-full text-sm p-3 border rounded-lg min-h-[70px] focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="contact-intro-input" />
          <p className="text-xs text-slate-400 mt-1.5">Affiché sous le titre « Nous contacter » en haut de la page</p>
        </CardContent>
      </Card>

      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <p className="text-sm text-blue-800 font-medium">📞 Téléphone, email et adresse</p>
          <p className="text-xs text-blue-700 mt-1">Ces coordonnées proviennent de <strong>Réglages → Entreprise</strong> (raison sociale, téléphone, email, adresse). Si un champ y est vide, la valeur d'<strong>Identité du site</strong> est utilisée en secours. Modifiez-les là-bas, elles se mettront à jour ici automatiquement.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Cartes coordonnées (sous-textes)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Note sous le téléphone</Label>
            <Input value={v('phone_note')} onChange={e => set('phone_note', e.target.value)} placeholder="Du mardi au samedi" className="text-sm" data-testid="contact-phone-note" />
          </div>
          <div>
            <Label className="text-xs">Note sous l'email</Label>
            <Input value={v('email_note')} onChange={e => set('email_note', e.target.value)} placeholder="Réponse sous 24h" className="text-sm" data-testid="contact-email-note" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Titre carte parking</Label>
              <Input value={v('parking_title')} onChange={e => set('parking_title', e.target.value)} placeholder="Grand parking gratuit" className="text-sm" />
            </div>
            <div>
              <Label className="text-xs">Texte parking</Label>
              <Input value={v('parking_text')} onChange={e => set('parking_text', e.target.value)} placeholder="Accès facile et gratuit" className="text-sm" />
            </div>
            <div>
              <Label className="text-xs">Note parking</Label>
              <Input value={v('parking_note')} onChange={e => set('parking_note', e.target.value)} placeholder="Plus de 50 places disponibles" className="text-sm" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Accès routier</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Titre accès</Label>
            <Input value={v('access_title')} onChange={e => set('access_title', e.target.value)} placeholder="Accès depuis l'autoroute" className="text-sm" />
          </div>
          <div>
            <Label className="text-xs">Texte accès</Label>
            <textarea value={v('access_text')} onChange={e => set('access_text', e.target.value)}
              placeholder={'Sortie autoroute A7, direction centre-ville. Suivre la signalétique "MAGERE Viandes".'}
              className="w-full text-sm p-2 border rounded-lg min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <Label className="text-xs">Détail parking</Label>
            <textarea value={v('parking_detail')} onChange={e => set('parking_detail', e.target.value)}
              placeholder="Grand parking gratuit de plus de 50 places juste devant le magasin..."
              className="w-full text-sm p-2 border rounded-lg min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Bloc « Livraison à domicile »</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Titre du bloc</Label>
            <Input value={v('delivery_title')} onChange={e => set('delivery_title', e.target.value)} placeholder="Livraison à domicile" className="text-sm" data-testid="contact-delivery-title" />
          </div>
          <div>
            <Label className="text-xs">Texte personnalisé (optionnel)</Label>
            <textarea value={pd.delivery_text || ''} onChange={e => set('delivery_text', e.target.value)}
              placeholder="Laissez vide pour l'affichage automatique des zones (noms, jours, seuil offert). Si vous écrivez un texte ici, il remplace l'affichage automatique."
              className="w-full text-sm p-2 border rounded-lg min-h-[70px] focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="contact-delivery-text" />
            <p className="text-xs text-slate-400 mt-1">Vide = les zones sont affichées automatiquement depuis Règles de livraison (recommandé, toujours à jour).</p>
          </div>
          <div>
            <Label className="text-xs">Petite note sous les zones</Label>
            <Input value={v('delivery_footnote')} onChange={e => set('delivery_footnote', e.target.value)} placeholder="La liste des communes desservies s'affiche automatiquement au moment de la commande selon votre code postal." className="text-sm" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Section « Professionnels » (bas de page)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Titre</Label>
            <Input value={v('pro_title')} onChange={e => set('pro_title', e.target.value)} placeholder="Vous êtes un professionnel ?" className="text-sm" data-testid="contact-pro-title" />
          </div>
          <div>
            <Label className="text-xs">Texte</Label>
            <textarea value={v('pro_text')} onChange={e => set('pro_text', e.target.value)}
              placeholder="Restaurateurs, traiteurs, collectivités : bénéficiez de nos tarifs professionnels..."
              className="w-full text-sm p-2 border rounded-lg min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="contact-pro-text" />
          </div>
          <p className="text-xs text-slate-400">Les boutons « Appelez-nous » et « Demander un devis » utilisent automatiquement le téléphone et l'email de Réglages → Entreprise.</p>
        </CardContent>
      </Card>

      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <p className="text-sm text-blue-800 font-medium">🕐 Horaires d'ouverture</p>
          <p className="text-xs text-blue-700 mt-1">Les horaires affichés sur la page contact sont <strong>dynamiques</strong> : gérés dans <strong>Configuration → Entreprise → Horaires d'ouverture</strong>. Chaque jour de la semaine s'affiche individuellement (lundi → dimanche).</p>
        </CardContent>
      </Card>
    </div>
  );
};

// ======== PAGE FORM ========
const PageForm = ({ page, storeId, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title: page?.title || '',
    slug: page?.slug || '',
    content_html: page?.content_html || page?.content || '',
    content_text: page?.content_text || '',
    meta_title: page?.meta_title || '',
    meta_description: page?.meta_description || '',
    is_published: page?.is_published ?? true,
    sort_order: page?.sort_order || 0,
    page_data: page?.page_data || null,
  });
  const [saving, setSaving] = useState(false);

  const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9àâäéèêëïîôùûüÿç]+/g, '-').replace(/(^-|-$)/g, '');

  const handleSave = async () => {
    if (!form.title) { toast.error('Titre requis'); return; }
    if (!form.slug) form.slug = autoSlug(form.title);
    setSaving(true);
    try {
      if (page?.id) {
        await axios.put(`${API_URL}/api/stores/${storeId}/pages/${page.id}`, form, { withCredentials: true });
        toast.success('Page mise à jour');
      } else {
        await axios.post(`${API_URL}/api/stores/${storeId}/pages`, form, { withCredentials: true });
        toast.success('Page créée');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="page-form">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onCancel}><ArrowLeft size={20} /></Button>
          <h2 className="text-xl font-bold">{page?.id ? 'Modifier la page' : 'Nouvelle page'}</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch checked={form.is_published} onCheckedChange={v => setForm(p => ({ ...p, is_published: v }))} />
            <Label className="text-sm">{form.is_published ? 'Publiée' : 'Brouillon'}</Label>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-page-btn">
            {saving ? <CircleNotch className="animate-spin mr-2" size={16} /> : <FloppyDisk className="mr-2" size={16} />}
            Sauvegarder
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <Card>
            <CardContent className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Titre *</Label>
                  <Input
                    value={form.title}
                    onChange={e => { setForm(p => ({ ...p, title: e.target.value })); if (!page?.id) setForm(p => ({ ...p, slug: autoSlug(e.target.value) })); }}
                    placeholder="Ex: À propos"
                    data-testid="page-title-input"
                  />
                </div>
                <div>
                  <Label>Slug</Label>
                  <Input value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value }))} placeholder="a-propos" />
                </div>
              </div>
            </CardContent>
          </Card>

          {!['contact', 'le-magasin'].includes(form.slug) && (
            <Card>
              <CardHeader><CardTitle className="text-base">Contenu</CardTitle></CardHeader>
              <CardContent>
                <HtmlEditor value={form.content_html} onChange={v => setForm(p => ({ ...p, content_html: v }))} />
              </CardContent>
            </Card>
          )}

          {/* Page Data Editor — for structured pages like "le-magasin" */}
          {(form.slug === 'le-magasin' || (form.page_data && form.slug !== 'contact')) && (
            <PageDataEditor
              pageData={form.page_data}
              onChange={pd => setForm(p => ({ ...p, page_data: pd }))}
            />
          )}

          {/* Contact Data Editor — textes + coordonnées de la page contact */}
          {form.slug === 'contact' && (
            <ContactDataEditor
              pageData={form.page_data}
              onChange={pd => setForm(p => ({ ...p, page_data: pd }))}
            />
          )}
        </div>

        <div className="space-y-5">
          <Card>
            <CardHeader><CardTitle className="text-base">SEO</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Meta titre</Label>
                <Input value={form.meta_title} onChange={e => setForm(p => ({ ...p, meta_title: e.target.value }))} placeholder="Titre pour Google" />
                <p className="text-xs text-slate-500 mt-1">{(form.meta_title || '').length}/60</p>
              </div>
              <div>
                <Label>Meta description</Label>
                <textarea className="w-full border rounded-lg p-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500" value={form.meta_description} onChange={e => setForm(p => ({ ...p, meta_description: e.target.value }))} placeholder="Description pour Google" />
                <p className="text-xs text-slate-500 mt-1">{(form.meta_description || '').length}/160</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Options</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Ordre d'affichage</Label>
                <Input type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} />
              </div>
              {page?.source_url && (
                <div className="p-2 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Importée depuis</p>
                  <a href={page.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 truncate block">{page.source_url}</a>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ======== MAIN PAGE ========
const CmsPagesPage = () => {
  const { currentStore } = useStore();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null); // null = list, 'new' = new, {page} = edit

  const fetchPages = useCallback(async () => {
    if (!currentStore) return;
    try {
      const res = await axios.get(`${API_URL}/api/stores/${currentStore.id}/pages`, { withCredentials: true });
      setPages(res.data);
    } catch { } finally { setLoading(false); }
  }, [currentStore]);

  useEffect(() => { fetchPages(); }, [fetchPages]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette page ?')) return;
    try {
      await axios.delete(`${API_URL}/api/stores/${currentStore.id}/pages/${id}`, { withCredentials: true });
      toast.success('Page supprimée');
      fetchPages();
    } catch { toast.error('Erreur'); }
  };

  const handleEdit = async (pageId) => {
    try {
      const res = await axios.get(`${API_URL}/api/stores/${currentStore.id}/pages/${pageId}`, { withCredentials: true });
      setEditingPage(res.data);
    } catch { toast.error('Erreur'); }
  };

  if (!currentStore) return <div className="text-center py-12 text-slate-500">Sélectionnez une boutique</div>;

  if (editingPage !== null) {
    return (
      <PageForm
        page={editingPage === 'new' ? null : editingPage}
        storeId={currentStore.id}
        onSave={() => { setEditingPage(null); fetchPages(); }}
        onCancel={() => setEditingPage(null)}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="cms-pages-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Pages du site</h1>
          <p className="text-sm text-slate-500 mt-1">Gérez le contenu des pages du site. Les pages <strong>Le magasin</strong> et <strong>Nous contacter</strong> ont un éditeur visuel dédié (textes, horaires, accès routier, photos...).</p>
        </div>
        <Button onClick={() => setEditingPage('new')} className="bg-[#0052FF]" data-testid="new-page-btn">
          <Plus className="mr-1" size={16} /> Nouvelle page
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><CircleNotch className="animate-spin mx-auto" size={24} /></div>
          ) : pages.length === 0 ? (
            <div className="p-12 text-center">
              <FileText size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Aucune page</p>
              <p className="text-sm text-slate-400 mt-2">Créez des pages ou importez-les depuis WordPress</p>
              <Button onClick={() => setEditingPage('new')} variant="outline" className="mt-4">
                <Plus className="mr-1" size={14} /> Créer une page
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titre</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Modifié</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pages.map(page => (
                  <TableRow key={page.id} className="cursor-pointer hover:bg-slate-50" onClick={() => handleEdit(page.id)}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-slate-400" />
                        <span className="font-medium">{page.title}</span>
                        {(page.slug === 'le-magasin' || page.slug === 'contact') && (
                          <Badge className="text-[10px] bg-pink-100 text-pink-700 hover:bg-pink-100">Éditeur visuel</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell><code className="text-xs bg-slate-100 px-2 py-0.5 rounded">/{page.slug}</code></TableCell>
                    <TableCell>
                      {page.source_url ? (
                        <div className="flex items-center gap-1 text-xs text-blue-600"><Globe size={12} />WordPress</div>
                      ) : (
                        <span className="text-xs text-slate-400">Manuel</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={page.is_published ? 'default' : 'secondary'}>
                        {page.is_published ? 'Publiée' : 'Brouillon'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {page.updated_at ? new Date(page.updated_at).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleEdit(page.id); }}>
                          <PencilSimple size={16} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDelete(page.id); }}>
                          <Trash size={16} className="text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CmsPagesPage;
